import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Pencil, Trash2, PackageCheck, Plus, Weight } from 'lucide-react';
import { Badge, Button, Container, Dialog, Empty, Stack, useToast } from '@easymoving/ui';
import { useBackend } from '../../lib/backend/context';
import { useBoxes, useItems } from '../../hooks/useUserData';
import { ItemCard } from '../../components/items/ItemCard';
import { BoxFormDialog } from '../../components/boxes/BoxFormDialog';
import { destinationTone, statusTone } from '../../lib/taxonomy/tone';
import { HEAVY_BOX_KG, boxWeightKg } from '../../lib/query/measure';
import { useUnits } from '../../lib/units/useUnits';

export function BoxDetailPage() {
  const { t } = useTranslation();
  const backend = useBackend();
  const { boxId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { boxes, loading } = useBoxes();
  const { items } = useItems();
  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const units = useUnits();
  const box = boxes.find((b) => b.id === boxId);
  const inBox = useMemo(() => items.filter((i) => i.boxId === boxId), [items, boxId]);
  const unassigned = useMemo(() => items.filter((i) => !i.boxId), [items]);
  const kg = boxId ? boxWeightKg(items, boxId) : 0;

  if (loading) {
    return (
      <Container className="pt-6">
        <p className="text-graphite py-12">{t('common.loading')}</p>
      </Container>
    );
  }
  if (!box || !boxId) {
    return (
      <Container className="pt-10">
        <Empty title={t('notFound.title')} body={t('notFound.body')} />
      </Container>
    );
  }

  async function markPacked() {
    if (!boxId) return;
    await Promise.all([
      backend.updateBox(boxId, { progressStatus: 'packed' }),
      ...inBox.map((i) => backend.updateItem(i.id, { progressStatus: 'packed' })),
    ]);
    toast.push({ title: t('boxes.markedPacked'), tone: 'success' });
  }

  async function onDelete() {
    if (!boxId) return;
    // Detach items (don't delete them), then remove the box.
    await Promise.all(inBox.map((i) => backend.updateItem(i.id, { boxId: undefined })));
    await backend.deleteBox(boxId);
    toast.push({ title: t('boxes.deleted'), tone: 'success' });
    navigate('/app/boxes');
  }

  async function addSelected() {
    if (!boxId) return;
    await Promise.all([...selected].map((id) => backend.updateItem(id, { boxId })));
    setSelected(new Set());
    setAssigning(false);
  }

  return (
    <Container size="narrow" className="pt-4 pb-8">
      <Link to="/app/boxes" className="text-petrol text-[14px] hover:underline">
        ← {t('boxes.title')}
      </Link>

      <div className="mt-3 surface p-5">
        <Stack gap="md">
          <div>
            <h1 className="font-display text-2xl tracking-snug">{box.name}</h1>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {box.destination && (
                <Badge tone={destinationTone(box.destination)}>{t(`enum.destination.${box.destination}`)}</Badge>
              )}
              {box.progressStatus && (
                <Badge tone={statusTone(box.progressStatus)}>{t(`enum.status.${box.progressStatus}`)}</Badge>
              )}
              {box.storageLocation && <Badge tone="neutral">{box.storageLocation}</Badge>}
              {kg > 0 && (
                <Badge tone="neutral">
                  <Weight className="size-3" />
                  {units.formatWeight(kg)}
                </Badge>
              )}
            </div>
            {kg > HEAVY_BOX_KG && (
              <div
                className="mt-3 flex items-start gap-2 rounded-lg bg-amber-tint text-amber px-3 py-2 text-[13px]"
                data-testid="heavy-box-warning"
              >
                <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                {t('boxes.tooHeavyHint', { limit: HEAVY_BOX_KG })}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setAssigning(true)}>
              <Plus className="size-4" />
              {t('boxes.assignItems')}
            </Button>
            <Button variant="secondary" onClick={markPacked}>
              <PackageCheck className="size-4" />
              {t('boxes.markPacked')}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(true)}>
              <Pencil className="size-4" />
              {t('boxes.edit')}
            </Button>
            <Button variant="ghost" className="text-clay" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="size-4" />
              {t('boxes.delete')}
            </Button>
          </div>
        </Stack>
      </div>

      <h2 className="smallcaps text-graphite mt-6 mb-3">{t('boxes.itemsInBox')}</h2>
      {inBox.length === 0 ? (
        <p className="text-graphite-soft text-[14px] surface-tint p-4">{t('boxes.noItems')}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="box-items">
          {inBox.map((item) => (
            <ItemCard key={item.id} item={item} />
          ))}
        </div>
      )}

      <BoxFormDialog open={editing} onOpenChange={setEditing} initial={box} />

      {/* Assign items dialog */}
      <Dialog
        open={assigning}
        onOpenChange={setAssigning}
        title={t('boxes.assignItems')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAssigning(false)}>
              {t('boxes.cancel')}
            </Button>
            <Button onClick={addSelected} disabled={selected.size === 0}>
              {t('boxes.addSelected')}
            </Button>
          </div>
        }
      >
        {unassigned.length === 0 ? (
          <p className="text-graphite text-[14px]">{t('boxes.noUnassigned')}</p>
        ) : (
          <Stack gap="xs">
            {unassigned.map((it) => (
              <label key={it.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-surface-tint cursor-pointer">
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--color-petrol)]"
                  checked={selected.has(it.id)}
                  onChange={(e) => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(it.id);
                      else next.delete(it.id);
                      return next;
                    });
                  }}
                />
                {it.primaryThumbUrl ? (
                  <img src={it.primaryThumbUrl} alt="" className="size-9 rounded object-cover" />
                ) : (
                  <span className="size-9 rounded bg-surface-tint" />
                )}
                <span className="text-[14px]">{it.name}</span>
              </label>
            ))}
          </Stack>
        )}
      </Dialog>

      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('boxes.deleteConfirm')}
        description={t('boxes.deleteConfirmBody')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t('boxes.cancel')}
            </Button>
            <Button variant="danger" onClick={onDelete}>
              {t('boxes.delete')}
            </Button>
          </div>
        }
      >
        <p className="text-graphite text-[14px]">{box.name}</p>
      </Dialog>
    </Container>
  );
}
